import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useBranchContext } from "@/hooks/useBranchContext";
import { useOrganizationContext } from "@/hooks/useOrganizationContext";

export interface Room { id: string; room_name: string; patient_count: number; avg_tat: string | null; }

const chunkArray = <T,>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) chunks.push(items.slice(i, i + size));
  return chunks;
};

export const useRooms = () => {
  const { currentBranchId } = useBranchContext();
  const { organizationId } = useOrganizationContext();
  return useQuery({
    queryKey: ["rooms", organizationId, currentBranchId],
    queryFn: async () => {
      if (currentBranchId == null || !organizationId) return [];
      const { data: patients, error } = await supabase.from("patients").select("id, room")
        .not("room", "is", null).is("physical_discharge_completion_time", null)
        .eq("branch_id", currentBranchId).eq("organization_id", organizationId);
      if (error) throw error;

      const patientIds = patients.map(p => p.id);
      if (patientIds.length === 0) return [];

      const activities: { patient_id: string }[] = [];
      for (const chunk of chunkArray(patientIds, 50)) {
        const { data, error: e } = await supabase.from("activities").select("patient_id")
          .in("patient_id", chunk).in("status", ["pending", "in-progress"])
          .eq("branch_id", currentBranchId).eq("organization_id", organizationId);
        if (e) throw e;
        if (data?.length) activities.push(...data);
      }

      const patientsWithActiveActivities = new Set(activities.map(a => a.patient_id));
      if (patientsWithActiveActivities.size === 0) return [];
      const filteredPatients = patients.filter(p => patientsWithActiveActivities.has(p.id));

      const inProgressActivities: { patient_id: string; assigned_at: string | null; started_at: string | null }[] = [];
      for (const chunk of chunkArray(Array.from(patientsWithActiveActivities), 50)) {
        const { data, error: e } = await supabase.from("activities").select("patient_id, assigned_at, started_at")
          .in("patient_id", chunk).eq("status", "in-progress")
          .eq("branch_id", currentBranchId).eq("organization_id", organizationId);
        if (e) throw e;
        if (data?.length) inProgressActivities.push(...data);
      }

      const patientMaxElapsedMap = new Map<string, number>();
      inProgressActivities.forEach((activity) => {
        const startTimestamp = activity.assigned_at || activity.started_at;
        if (startTimestamp) {
          const elapsedMs = new Date().getTime() - new Date(startTimestamp).getTime();
          patientMaxElapsedMap.set(activity.patient_id, Math.max(patientMaxElapsedMap.get(activity.patient_id) || 0, elapsedMs));
        }
      });

      const roomTATMap = new Map<string, number[]>();
      filteredPatients.forEach((patient) => {
        const maxElapsed = patientMaxElapsedMap.get(patient.id);
        if (maxElapsed !== undefined && patient.room) {
          const times = roomTATMap.get(patient.room) || [];
          times.push(maxElapsed);
          roomTATMap.set(patient.room, times);
        }
      });

      const roomMap = new Map<string, number>();
      filteredPatients.forEach((patient) => {
        if (patient.room) roomMap.set(patient.room, (roomMap.get(patient.room) || 0) + 1);
      });

      const formatAvgTAT = (elapsedTimes: number[]): string => {
        if (elapsedTimes.length === 0) return "00:00";
        const avgMs = elapsedTimes.reduce((a, b) => a + b, 0) / elapsedTimes.length;
        const hours = Math.floor(avgMs / (1000 * 60 * 60));
        const minutes = Math.floor((avgMs % (1000 * 60 * 60)) / (1000 * 60));
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      };

      return Array.from(roomMap.entries())
        .map(([room_name, patient_count]) => ({
          id: room_name, room_name, patient_count,
          avg_tat: formatAvgTAT(roomTATMap.get(room_name) || []),
        }))
        .sort((a, b) => a.room_name.localeCompare(b.room_name));
    },
    enabled: currentBranchId != null && !!organizationId,
  });
};
